import { Box, IconButton, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Responsive, WidthProvider } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import useAPI from "../../../api/hooks/useAPI";
import useColors from "../../../api/hooks/useColors";
import CustomTooltip from "../../../components/CustomTooltip";
import LoadingIcon from "../../../components/LoadingIcon";
import SvgToIcon from "../../../icons/SvgToIcon";
import { TimePoint } from "../../../utils/constants";
import { icons } from "../../stack/environment/icons/iconExport";
import ControlDrawerWrapper from "../ControlDrawerWrapper";
import { useDashboard } from "../hooks/useDashboard";
import { useWidgetPdfExport } from "./hooks/useWidgetPdfExport";
import TrendSelector from "./TrendSelector";
import { GRID_CONFIG } from "./widgetDefinitions";
import FavoriteControls from "./widgets/ControlsTableWidgets/FavoriteControls";
import MyTasks from "./widgets/ControlsTableWidgets/MyTasks";
import TeamTasks from "./widgets/ControlsTableWidgets/TeamTasks";
import TopOpenGaps from "./widgets/ControlsTableWidgets/TopOpenGaps";
import CriticalAssetPieWidget from "./widgets/CriticalAssetPieWidget/CriticalAssetPieWidget";
import CustomWidget from "./widgets/CustomWidget/CustomWidget";
import HeatmapWidget from "./widgets/HeatmapWidget/HeatmapWidget";
import InfrastructurePillarsScoreWidget from "./widgets/InfrastructurePillarsScoreWidget/InfrastructurePillarsScoreWidget";
import InventoryWidget from "./widgets/InventoryAssetsWidget/InventoryAssetsWidget";
import OpenGapsAgeWidget from "./widgets/OpenGapsAgeWidget/OpenGapsAgeWidget";
import PostureTrendWidget from "./widgets/PostureTrendWidget/PostureTrendWidget";
import SecurityDomainScoreWidget from "./widgets/SecurityDomainScoreWidget/SecurityDomainScoreWidget";
import SecurityScoreWidget from "./widgets/SecurityScoreWidgets/SecurityScoreWidget";
import TeamRemediationPerformanceWidget from "./widgets/TeamRemediationPerformance/TeamRemediationPerformanceWidget";
import ToolCoverageWidget from "./widgets/ToolCoveragewidget/ToolCoverageWidget";
import ToolsPerformanceTrendWidget from "./widgets/ToolsPerformanceTrendWidget/ToolsPerformanceTrendWidget";
import ToolsRemediationPerformanceWidget from "./widgets/ToolsRemediationPerformance/ToolsRemediationPerformanceWidget";
import WidgetSelector from "./WidgetSelector";
import WidgetWrapper from "./WidgetWrapper";
const ResponsiveReactGridLayout = WidthProvider(Responsive);

// Widget content renderer
const WidgetContent = ({
  widget,
  baseTimePoint,
  widgetRef,
  registerWidgetExport,
}) => {
  // Register widget export functions when the component mounts
  useEffect(() => {
    const currentRef = widgetRef?.current;
    if (currentRef?.hasExportCapability) {
      registerWidgetExport(widget.id, currentRef.exportToPdf);
    }
    return () => {
      // Cleanup when component unmounts
      if (currentRef?.hasExportCapability) {
        registerWidgetExport(widget.id, null);
      }
    };
  }, [widget.id, widgetRef.current]);

  if (widget.contentType === "text") {
    return (
      <WidgetWrapper title={widget.title} viewAllUrl={widget.viewAllUrl}>
        {widget.description}
      </WidgetWrapper>
    );
  }

  if (widget.contentType === "component") {
    switch (widget.componentType) {
      case "OperationalHeatmap":
        return <HeatmapWidget ref={widgetRef} mainTimePoint={baseTimePoint} />;
      case "TopOpenGaps":
        return <TopOpenGaps />;
      case "TeamTasks":
        return <TeamTasks />;
      case "FavoriteControls":
        return <FavoriteControls />;
      case "MyTasks":
        return <MyTasks />;
      case "InventoryWidget":
        return (
          <InventoryWidget ref={widgetRef} mainTimePoint={baseTimePoint} />
        );
      case "ToolsPerformanceTrendWidget":
        return (
          <ToolsPerformanceTrendWidget
            ref={widgetRef}
            mainTimePoint={baseTimePoint}
          />
        );
      case "CriticalAssetPieWidget":
        return (
          <CriticalAssetPieWidget
            ref={widgetRef}
            mainTimePoint={baseTimePoint}
          />
        );
      case "ToolCoverageWidget":
        return (
          <ToolCoverageWidget ref={widgetRef} mainTimePoint={baseTimePoint} />
        );
      case "PostureTrendWidget":
        return (
          <PostureTrendWidget ref={widgetRef} mainTimePoint={baseTimePoint} />
        );
      case "ToolsRemediationPerformanceWidget":
        return (
          <ToolsRemediationPerformanceWidget
            ref={widgetRef}
            mainTimePoint={baseTimePoint}
          />
        );
      case "TeamRemediationPerformanceWidget":
        return (
          <TeamRemediationPerformanceWidget
            ref={widgetRef}
            mainTimePoint={baseTimePoint}
          />
        );
      case "SecurityDomainScoreWidget":
        return (
          <SecurityDomainScoreWidget
            ref={widgetRef}
            mainTimePoint={baseTimePoint}
          />
        );
      case "InfrastructurePillarsScoreWidget":
        return (
          <InfrastructurePillarsScoreWidget
            ref={widgetRef}
            mainTimePoint={baseTimePoint}
          />
        );
      case "SecurityScoreWidget":
        return (
          <SecurityScoreWidget ref={widgetRef} mainTimePoint={baseTimePoint} />
        );
      case "OpenGapsAgeWidget":
        return (
          <OpenGapsAgeWidget ref={widgetRef} mainTimePoint={baseTimePoint} />
        );
      case "CustomWidget":
        return <CustomWidget ref={widgetRef} widget={widget} />;
      default:
        return <Typography>Unknown component type</Typography>;
    }
  }

  return null;
};

const CustomDashboard = () => {
  const colors = useColors();
  const { trackEvent } = useAPI();
  const [baseTimePoint, setBaseTimePoint] = useState(TimePoint.LATEST);
  const [isExporting, setIsExporting] = useState(false);
  const {
    mounted,
    widgets,
    generateLayouts,
    isLoading,
    isError,
    onLayoutChange,
  } = useDashboard();

  // PDF export functionality
  const { registerWidgetExport, exportAllWidgetsPdf, exportCount } =
    useWidgetPdfExport();

  // Create refs for widgets that support PDF export
  const widgetRefs = useRef({});

  // Get or create ref for a widget
  const getWidgetRef = (widgetId) => {
    if (!widgetRefs.current[widgetId]) {
      widgetRefs.current[widgetId] = { current: null };
    }
    return widgetRefs.current[widgetId];
  };

  // Effect to handle export after re-render
  useEffect(() => {
    if (isExporting) {
      const performExport = async () => {
        await exportAllWidgetsPdf();
        setIsExporting(false);
      };
      performExport();
    }
  }, [isExporting, exportCount, exportAllWidgetsPdf]);

  // Handle export all widgets to PDF
  const handleExportAllToPdf = async () => {
    trackEvent("User clicked on export all widgets to PDF");

    // Trigger re-render and then export
    setIsExporting(true);
  };

  // Styles
  const styles = {
    dashboard: {
      fontFamily: "Poppins",
    },
    widgetSelector: {
      padding: "15px",
      marginTop: "-50px",
      backgroundColor: "transparent",
      borderRadius: "6px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      flexDirection: "row",
      justifyContent: "flex-end",
    },
    dashboardContainer: {
      minHeight: "600px",
      backgroundColor: "none",
      borderRadius: "12px",
      padding: "10px",
    },
    widget: {
      backgroundColor: "white",
      boxShadow: `0px 0px 2px 1px ${colors.grey_24percent}`,
      borderRadius: "12px",
      overflow: "hidden",
      height: "100%",
    },
    widgetHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: "4px",
      padding: "12px 4px",
    },
    widgetHeaderTitle: {
      display: "flex",
      alignItems: "center",
      flexDirection: "row",
      gap: "4px",
      justifyContent: "flex-start",
      cursor: "grab",
    },
    widgetTitle: {
      margin: 0,
      fontSize: "14px",
    },
    widgetContent: {
      height: "calc(100% - 45px)",
      overflow: "auto",
    },
  };

  // Custom CSS for drag colors
  const dragStyles = `
    .react-grid-item.react-grid-placeholder {
      background: ${colors.primary_50} !important;
      border: 1px dashed ${colors.primary_dark} !important;
      border-radius: 12px !important;
    }
    
    .react-grid-item.react-draggable-dragging {
      border: 1px solid ${colors.primary_8percent} !important;
      border-radius: 12px !important;
    }
    .react-grid-layout.layout {
      background: ${colors.common_white} !important;
    }
  `;

  // Show loading state
  if (isLoading) {
    return (
      <Box style={styles.dashboard}>
        <LoadingIcon />
      </Box>
    );
  }

  // Show error state
  if (isError) {
    return (
      <Box style={styles.dashboard}>
        <Typography color="error">
          Error loading dashboard. Please try again later.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box>
        <style>{dragStyles}</style>
        <Box style={styles.dashboard}>
          {/* Widget Selector */}
          <Box style={styles.widgetSelector}>
            <TrendSelector
              baseTimePoint={baseTimePoint}
              setBaseTimePoint={setBaseTimePoint}
            />

            {/* Export All PDF Button */}
            {exportCount > 0 && (
              <CustomTooltip title="Export all widgets to PDF" placement="top">
                <IconButton
                  onClick={handleExportAllToPdf}
                  sx={{
                    background: colors.common_white,
                    color: colors.action_active,
                    height: "32px",
                    width: "32px",
                    border: "1px solid " + colors.enabled_stroke,
                    borderRadius: "8px",
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      background: colors.grey_8percent,
                    },
                  }}
                >
                  <SvgToIcon
                    IconLogo={icons.ExportPDFIcon}
                    style={{ width: "18px" }}
                  />
                </IconButton>
              </CustomTooltip>
            )}

            <WidgetSelector widgets={widgets} />
          </Box>
          {/* Dashboard Grid */}
          <Box style={styles.dashboardContainer}>
            <ResponsiveReactGridLayout
              className="layout"
              layouts={generateLayouts}
              breakpoints={GRID_CONFIG.breakpoints}
              cols={GRID_CONFIG.cols}
              rowHeight={GRID_CONFIG.rowHeight}
              onLayoutChange={onLayoutChange}
              isDraggable={true}
              isResizable={false}
              measureBeforeMount={false}
              useCSSTransforms={mounted}
              style={{ background: colors.grey_100, borderRadius: "12px" }}
              compactType="vertical"
              preventCollision={false}
              margin={GRID_CONFIG.margin}
              containerPadding={GRID_CONFIG.containerPadding}
              draggableHandle={GRID_CONFIG.draggableHandle}
              resizeHandles={GRID_CONFIG.resizeHandles}
            >
              {widgets.map((widget) => {
                const widgetRef = getWidgetRef(widget.id);
                return (
                  <div key={widget.id}>
                    <Box style={{ ...styles.widget, height: "100%" }}>
                      <WidgetContent
                        widget={widget}
                        baseTimePoint={baseTimePoint}
                        widgetRef={widgetRef}
                        registerWidgetExport={registerWidgetExport}
                      />
                    </Box>
                  </div>
                );
              })}
            </ResponsiveReactGridLayout>
          </Box>
          <ControlDrawerWrapper />
        </Box>
      </Box>
    </Box>
  );
};

export default CustomDashboard;
